import { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { formatApiError, fileUrl } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  GraduationCap, PlayCircle, FileText, ExternalLink, CheckCircle2, XCircle, Clock,
  Plus, Trash2, AlertTriangle, ChevronRight, Users, Award, Upload, Link2, BarChart3,
} from 'lucide-react';
import TrainingModuleReportDialog from '../components/TrainingModuleReportDialog';

const fmt = (s) => { if (!s) return '—'; try { return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return s; } };

const statusPill = {
  completed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  failed:    'bg-red-100 text-red-700 border-red-200',
  pending:   'bg-amber-100 text-amber-700 border-amber-200',
};

export default function Training() {
  const { user } = useAuth();
  const roles = user?.roles || [user?.role].filter(Boolean);
  const isAdmin = roles.some(r => ['admin', 'hr'].includes(r));
  const { mid } = useParams();
  if (mid) return <TrainingDetail isAdmin={isAdmin} />;
  return <TrainingList isAdmin={isAdmin} />;
}

function TrainingList({ isAdmin }) {
  const nav = useNavigate();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [reportFor, setReportFor] = useState(null); // {id, title} | null
  const [deleteFor, setDeleteFor] = useState(null); // {id, title, total} | null
  const [deletingBusy, setDeletingBusy] = useState(false);

  /* eslint-disable */
  useEffect(() => { load(); }, []);
  /* eslint-enable */
  async function load() {
    setLoading(true);
    try { const { data } = await api.get('/training/modules'); setModules(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }

  return (
    <div className="space-y-6" data-testid="training-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Learning</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">Training modules</h1>
          <p className="text-slate-500 mt-1">{isAdmin ? 'Create & track mandatory and optional training for the team.' : 'Modules assigned to you. Click any card to begin.'}</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setCreateOpen(true)} data-testid="create-module-btn" className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]">
            <Plus className="w-4 h-4 mr-1.5" /> New module
          </Button>
        )}
      </div>

      {loading ? <div className="text-sm text-slate-400">Loading…</div> :
        modules.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-10 text-center text-slate-500" data-testid="no-modules">
            <GraduationCap className="w-10 h-10 mx-auto text-slate-300 mb-3" />
            {isAdmin ? 'No training modules yet. Create the first one!' : 'No modules assigned to you right now.'}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {modules.map(m => (
              <div key={m.id} data-testid={`module-card-${m.id}`}
                   onClick={() => nav(`/training/${m.id}`)}
                   className="bg-white border border-slate-200 rounded-2xl p-5 cursor-pointer hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-indigo-50 text-[#3D4A8E] flex items-center justify-center">
                    <GraduationCap className="w-5 h-5" />
                  </div>
                  {m.mandatory && (
                    <span className="text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">Mandatory</span>
                  )}
                </div>
                <h3 className="font-heading text-lg font-semibold text-slate-900 leading-snug">{m.title}</h3>
                {m.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{m.description}</p>}
                <div className="text-[11px] text-slate-500 mt-3 space-y-1">
                  {m.deadline && <div className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Due {fmt(m.deadline)}</div>}
                  <div>{m.materials?.length || 0} materials{(m.quiz?.length || m.quiz_question_count) ? ` · ${m.quiz?.length || m.quiz_question_count}-Q quiz` : ''}</div>
                </div>
                {isAdmin && m.progress && (
                  <div className="mt-3">
                    <div className="flex justify-between text-[11px] text-slate-500 mb-1">
                      <span>{m.progress.completed}/{m.progress.total} completed</span>
                      <span className="font-semibold">{m.progress.pct}%</span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-[#3D4A8E] transition-all" style={{ width: `${m.progress.pct}%` }} />
                    </div>
                    <div className="mt-3 flex gap-2">
                      <button
                        data-testid={`view-report-${m.id}`}
                        onClick={(e) => { e.stopPropagation(); setReportFor({ id: m.id, title: m.title }); }}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold text-[#3D4A8E] hover:bg-indigo-50 border border-indigo-200 rounded-full py-1.5 transition-colors">
                        <BarChart3 className="w-3.5 h-3.5" strokeWidth={2} /> View report
                      </button>
                      <button
                        data-testid={`delete-module-${m.id}`}
                        onClick={(e) => { e.stopPropagation(); setDeleteFor({ id: m.id, title: m.title, total: m.progress?.total || 0 }); }}
                        title="Delete this module"
                        className="inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold text-rose-700 hover:bg-rose-50 border border-rose-200 rounded-full py-1.5 px-3 transition-colors">
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
                      </button>
                    </div>
                  </div>
                )}
                {!isAdmin && (
                  <div className="mt-3 flex items-center justify-between">
                    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full border ${statusPill[m.my_status] || statusPill.pending}`}>
                      {m.my_status || 'pending'}{m.my_score != null ? ` · ${m.my_score}%` : ''}
                    </span>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      }

      <CreateModuleDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={() => { setCreateOpen(false); load(); }} />
      <TrainingModuleReportDialog
        moduleId={reportFor?.id}
        moduleTitle={reportFor?.title}
        open={!!reportFor}
        onOpenChange={(o) => !o && setReportFor(null)}
      />

      {/* ---- Delete module confirmation ---- */}
      <Dialog open={!!deleteFor} onOpenChange={(o) => !o && !deletingBusy && setDeleteFor(null)}>
        <DialogContent data-testid="confirm-delete-module-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-rose-600" strokeWidth={1.8} /> Delete this training module?
            </DialogTitle>
            <DialogDescription>
              You&apos;re about to permanently delete <b>{deleteFor?.title}</b> and all completion records for {deleteFor?.total || 0} assignee{(deleteFor?.total || 0) !== 1 ? 's' : ''}. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-xs text-rose-900">
            All progress, scores, and attempt history for this module will be wiped from the database.
            Audit log entry will record this action.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteFor(null)} disabled={deletingBusy} className="rounded-full" data-testid="cancel-delete-module">
              Cancel
            </Button>
            <Button
              data-testid="confirm-delete-module"
              disabled={deletingBusy}
              onClick={async () => {
                setDeletingBusy(true);
                try {
                  await api.delete(`/training/modules/${deleteFor.id}`);
                  toast.success(`"${deleteFor.title}" deleted`);
                  setDeleteFor(null);
                  load();
                } catch (e) {
                  toast.error(formatApiError(e.response?.data?.detail));
                } finally {
                  setDeletingBusy(false);
                }
              }}
              className="rounded-full bg-rose-600 hover:bg-rose-700 text-white">
              <Trash2 className="w-4 h-4 mr-1.5" strokeWidth={2.4} /> {deletingBusy ? 'Deleting…' : 'Yes, delete module'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CreateModuleDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({
    title: '', description: '', mandatory: true, deadline: '',
    scope: 'all', // 'all' | 'departments' | 'specific'
    default_for_new_hires: false,
  });
  const [assigneeDepartments, setAssigneeDepartments] = useState([]); // string[]
  const [assigneeEmails, setAssigneeEmails] = useState([]);           // string[]
  const [emailDraft, setEmailDraft] = useState('');
  const [allDepartments, setAllDepartments] = useState([]);
  const [materials, setMaterials] = useState([{ type: 'video_url', label: '', url: '', file_id: null, filename: null }]);
  const [hasQuiz, setHasQuiz] = useState(false);
  const [quiz, setQuiz] = useState([{ q: '', options: ['', '', '', ''], correct_index: 0 }]);
  const [busy, setBusy] = useState(false);
  const fileInputRefs = useRef({});

  // Load departments when the dialog opens so the picker has options.
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get('/departments');
        setAllDepartments((data || []).map(d => d.name).filter(Boolean));
      } catch { /* ignore — picker can still accept free-typed names */ }
    })();
  }, [open]);

  const uploadFile = async (i, file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/training/upload-material', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setMaterials(materials.map((x, j) => j === i ? { ...x, type: 'file', file_id: data.file_id, filename: data.filename, label: x.label || data.filename, url: '' } : x));
      toast.success(`Uploaded ${data.filename}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Upload failed'); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (form.scope === 'departments' && assigneeDepartments.length === 0) {
      toast.error('Pick at least one department, or choose a different audience'); return;
    }
    // Auto-flush a half-typed email when user hits submit.
    let emails = assigneeEmails.slice();
    const draft = emailDraft.trim().toLowerCase();
    if (form.scope === 'specific' && draft && /^[^\s@]+@[^\s@]+$/.test(draft)) {
      emails = [...emails, draft]; setEmailDraft('');
    }
    if (form.scope === 'specific' && emails.length === 0) {
      toast.error('Add at least one employee email, or choose a different audience'); return;
    }
    if (hasQuiz) {
      for (const q of quiz) {
        if (!q.q.trim() || q.options.some(o => !o.trim())) { toast.error('Fill all quiz fields'); return; }
      }
    }
    setBusy(true);
    try {
      await api.post('/training/modules', {
        title: form.title, description: form.description,
        materials: materials.filter(m => (m.url || m.file_id)).map(m => ({
          type: m.type, label: m.label || m.filename || 'Material',
          url: m.url || null, file_id: m.file_id || null,
        })),
        quiz: hasQuiz ? quiz : null,
        pass_score: 80,
        mandatory: form.mandatory, deadline: form.deadline || null,
        scope: form.scope,
        assignee_departments: form.scope === 'departments' ? assigneeDepartments : [],
        assignee_emails: form.scope === 'specific' ? emails : [],
        default_for_new_hires: form.default_for_new_hires,
      });
      const audienceText =
        form.scope === 'all' ? 'all employees' :
        form.scope === 'departments' ? `${assigneeDepartments.length} department${assigneeDepartments.length > 1 ? 's' : ''}` :
        `${emails.length} employee${emails.length > 1 ? 's' : ''}`;
      toast.success(`Module created${form.mandatory ? ` — emails sent to ${audienceText}` : ''}`);
      // reset
      setForm({ title: '', description: '', mandatory: true, deadline: '', scope: 'all', default_for_new_hires: false });
      setAssigneeDepartments([]); setAssigneeEmails([]); setEmailDraft('');
      setMaterials([{ type: 'video_url', label: '', url: '', file_id: null, filename: null }]);
      setHasQuiz(false); setQuiz([{ q: '', options: ['', '', '', ''], correct_index: 0 }]);
      onCreated();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const addEmail = (raw) => {
    const v = (raw || '').trim().toLowerCase();
    if (!v) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) { toast.error(`Invalid email: ${v}`); return; }
    if (assigneeEmails.includes(v)) { toast.error('Already added'); return; }
    setAssigneeEmails([...assigneeEmails, v]);
    setEmailDraft('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-module-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">New training module</DialogTitle>
          <DialogDescription>Add materials (videos / docs links). Optionally add an MCQ quiz; employees need 80% to complete.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-xs font-semibold text-slate-600">Title *</Label>
            <Input data-testid="tm-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-xl mt-1" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-600">Description</Label>
            <Textarea data-testid="tm-desc" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.mandatory} onChange={(e) => setForm({ ...form, mandatory: e.target.checked })} data-testid="tm-mandatory" />
              <span><b>Mandatory</b> for assignees</span>
            </label>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Deadline (optional)</Label>
              <Input data-testid="tm-deadline" type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} className="rounded-xl mt-1" />
            </div>
          </div>

          {/* ---------- Audience selector ---------- */}
          <div className="bg-indigo-50/40 border border-indigo-100 rounded-xl p-3 space-y-3" data-testid="audience-block">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-600">Who should take this module?</div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: 'all',         label: 'All employees',  hint: 'Everyone active'},
                { v: 'departments', label: 'By department',  hint: 'Pick one or more'},
                { v: 'specific',    label: 'Specific people', hint: 'Add by email'},
              ].map(opt => (
                <button key={opt.v} type="button" data-testid={`scope-${opt.v}`}
                        onClick={() => setForm({ ...form, scope: opt.v })}
                        className={`rounded-xl px-3 py-2 text-left border transition ${
                          form.scope === opt.v
                            ? 'bg-[#3D4A8E] text-white border-[#3D4A8E] shadow-sm'
                            : 'bg-white text-slate-700 border-slate-200 hover:border-[#3D4A8E]/40'
                        }`}>
                  <div className="text-xs font-bold">{opt.label}</div>
                  <div className={`text-[10px] mt-0.5 ${form.scope === opt.v ? 'text-indigo-100' : 'text-slate-500'}`}>{opt.hint}</div>
                </button>
              ))}
            </div>

            {form.scope === 'departments' && (
              <div data-testid="dept-picker">
                <Label className="text-xs font-semibold text-slate-600">Pick departments</Label>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {allDepartments.length === 0 && <div className="text-xs text-slate-500">No departments configured. Create one under <b>Departments</b> first.</div>}
                  {allDepartments.map(d => {
                    const picked = assigneeDepartments.includes(d);
                    return (
                      <button key={d} type="button" data-testid={`dept-chip-${d}`}
                              onClick={() => setAssigneeDepartments(picked ? assigneeDepartments.filter(x => x !== d) : [...assigneeDepartments, d])}
                              className={`text-xs px-3 py-1 rounded-full border transition ${
                                picked ? 'bg-[#3D4A8E] text-white border-[#3D4A8E]' : 'bg-white text-slate-700 border-slate-300 hover:border-[#3D4A8E]/40'
                              }`}>
                        {d}{picked && <span className="ml-1">×</span>}
                      </button>
                    );
                  })}
                </div>
                {assigneeDepartments.length > 0 && (
                  <div className="text-[11px] text-slate-500 mt-2">Assigns to <b className="text-slate-900">{assigneeDepartments.length}</b> department{assigneeDepartments.length > 1 ? 's' : ''}.</div>
                )}
              </div>
            )}

            {form.scope === 'specific' && (
              <div data-testid="email-picker">
                <Label className="text-xs font-semibold text-slate-600">Employee emails</Label>
                <div className="mt-1.5 flex flex-wrap gap-1.5 items-center bg-white rounded-xl border border-slate-200 px-2 py-2 min-h-[42px]">
                  {assigneeEmails.map(e => (
                    <span key={e} data-testid={`email-chip-${e}`}
                          className="inline-flex items-center gap-1 text-xs bg-indigo-50 text-[#3D4A8E] border border-indigo-200 rounded-full px-2.5 py-1">
                      {e}
                      <button type="button" onClick={() => setAssigneeEmails(assigneeEmails.filter(x => x !== e))}
                              className="text-[#3D4A8E] hover:text-rose-600">×</button>
                    </span>
                  ))}
                  <input data-testid="email-input" value={emailDraft}
                         onChange={(e) => setEmailDraft(e.target.value)}
                         onKeyDown={(e) => {
                           if (['Enter', ',', ' '].includes(e.key)) { e.preventDefault(); addEmail(emailDraft); }
                           else if (e.key === 'Backspace' && !emailDraft && assigneeEmails.length) {
                             setAssigneeEmails(assigneeEmails.slice(0, -1));
                           }
                         }}
                         onBlur={() => emailDraft && addEmail(emailDraft)}
                         placeholder={assigneeEmails.length ? '' : 'type email + Enter…'}
                         className="flex-1 min-w-[160px] outline-none text-sm bg-transparent" />
                </div>
                <div className="text-[11px] text-slate-500 mt-1.5">
                  Tip: press <kbd className="px-1 py-0.5 bg-slate-100 rounded text-[10px]">Enter</kbd> or comma to add. Personal or work emails are both accepted.
                </div>
              </div>
            )}

            <label className="flex items-center gap-2 text-xs text-slate-600 pt-1 border-t border-indigo-100">
              <input type="checkbox" checked={form.default_for_new_hires}
                     onChange={(e) => setForm({ ...form, default_for_new_hires: e.target.checked })}
                     data-testid="tm-new-hires" />
              <span>Also auto-assign to <b>new hires</b> added in the future</span>
            </label>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">Materials — add PDFs, videos, or external links</div>
            {materials.map((m, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-lg p-3 mb-2">
                <div className="flex gap-2 items-center mb-2">
                  <Input placeholder="Label (e.g. Safety policy PDF)" value={m.label}
                         onChange={(e) => setMaterials(materials.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} className="rounded-xl flex-1" />
                  <Button size="icon" variant="ghost" onClick={() => setMaterials(materials.filter((_, j) => j !== i))} className="rounded-full h-9 w-9 flex-shrink-0"><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                </div>
                {m.file_id ? (
                  <div className="flex items-center justify-between gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-2 py-1.5 text-xs" data-testid={`material-file-${i}`}>
                    <span className="inline-flex items-center gap-1.5 text-emerald-800 truncate"><FileText className="w-3.5 h-3.5" /> {m.filename}</span>
                    <button onClick={() => setMaterials(materials.map((x, j) => j === i ? { ...x, file_id: null, filename: null, type: 'video_url' } : x))} className="text-red-500 text-[10px] uppercase font-bold">Remove</button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input placeholder="https://… (video / external link)" value={m.url}
                           onChange={(e) => setMaterials(materials.map((x, j) => j === i ? { ...x, url: e.target.value, type: 'video_url' } : x))}
                           className="rounded-xl flex-1 text-sm h-9" />
                    <span className="text-xs text-slate-400 self-center">or</span>
                    <input type="file" accept=".pdf,.mp4,.mov,.avi,.png,.jpg,.jpeg,.docx,.pptx" className="hidden"
                           ref={(el) => { fileInputRefs.current[i] = el; }}
                           onChange={(e) => uploadFile(i, e.target.files?.[0])} />
                    <Button size="sm" variant="outline" onClick={() => fileInputRefs.current[i]?.click()} disabled={busy}
                            data-testid={`material-upload-${i}`} className="rounded-full h-9 flex-shrink-0">
                      <Upload className="w-3.5 h-3.5 mr-1" /> Upload file
                    </Button>
                  </div>
                )}
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={() => setMaterials([...materials, { type: 'video_url', label: '', url: '', file_id: null, filename: null }])} className="rounded-full h-7"><Plus className="w-3 h-3 mr-1" /> Add material</Button>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={hasQuiz} onChange={(e) => setHasQuiz(e.target.checked)} data-testid="tm-has-quiz" />
            <span><b>Add a quiz</b> — employees need 80% to complete</span>
          </label>

          {hasQuiz && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-3">
              {quiz.map((q, qi) => (
                <div key={qi} className="bg-white rounded-lg p-3 border border-amber-100">
                  <div className="flex justify-between items-center mb-2">
                    <div className="text-xs font-bold text-slate-600">Question {qi + 1}</div>
                    {quiz.length > 1 && (
                      <Button size="icon" variant="ghost" onClick={() => setQuiz(quiz.filter((_, j) => j !== qi))} className="h-6 w-6"><Trash2 className="w-3 h-3 text-red-500" /></Button>
                    )}
                  </div>
                  <Input placeholder="Question text" value={q.q}
                         onChange={(e) => setQuiz(quiz.map((x, j) => j === qi ? { ...x, q: e.target.value } : x))} className="rounded-xl mb-2" />
                  {q.options.map((opt, oi) => (
                    <div key={oi} className="flex items-center gap-2 mb-1">
                      <input type="radio" name={`correct-${qi}`} checked={q.correct_index === oi}
                             onChange={() => setQuiz(quiz.map((x, j) => j === qi ? { ...x, correct_index: oi } : x))} />
                      <Input placeholder={`Option ${oi + 1}`} value={opt}
                             onChange={(e) => setQuiz(quiz.map((x, j) => j === qi ? { ...x, options: x.options.map((o, k) => k === oi ? e.target.value : o) } : x))}
                             className="rounded-xl text-sm h-9 flex-1" />
                    </div>
                  ))}
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => setQuiz([...quiz, { q: '', options: ['', '', '', ''], correct_index: 0 }])} className="rounded-full h-7"><Plus className="w-3 h-3 mr-1" /> Add question</Button>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="tm-submit" className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]">
            {busy ? 'Creating…' : 'Create module'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TrainingDetail({ isAdmin }) {
  const { mid } = useParams();
  const nav = useNavigate();
  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [completions, setCompletions] = useState(null);

  /* eslint-disable */
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/training/modules/${mid}`);
        setModule(data);
        if (isAdmin) {
          const { data: comp } = await api.get(`/training/modules/${mid}/completions`);
          setCompletions(comp);
        }
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Failed to load'); }
      finally { setLoading(false); }
    })();
  }, [mid, isAdmin]);
  /* eslint-enable */

  if (loading) return <div className="text-sm text-slate-400">Loading…</div>;
  if (!module) return null;

  const submitQuiz = async () => {
    const arr = module.quiz.map((_, i) => answers[i] ?? -1);
    if (arr.some(a => a === -1)) { toast.error('Please answer all questions'); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/training/modules/${mid}/submit-quiz`, { answers: arr });
      setResult(data);
      if (data.passed) toast.success(`Passed with ${data.score}%! 🎉`);
      else toast.error(`Score ${data.score}% — need ${data.pass_score}% to pass. Try again.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const markDone = async () => {
    setSubmitting(true);
    try {
      await api.post(`/training/modules/${mid}/mark-done`);
      toast.success('Marked complete ✓');
      const { data } = await api.get(`/training/modules/${mid}`);
      setModule(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const myStatus = module.my_completion?.status;

  return (
    <div className="space-y-6" data-testid="module-detail">
      <button onClick={() => nav('/training')} className="text-xs text-slate-500 hover:text-[#3D4A8E]">&larr; Back to training</button>

      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-12 h-12 rounded-xl bg-indigo-50 text-[#3D4A8E] flex items-center justify-center"><GraduationCap className="w-6 h-6" /></div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              {module.mandatory && <span className="text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">Mandatory</span>}
              {module.deadline && <span className="text-[10px] text-slate-500 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Due {fmt(module.deadline)}</span>}
              {myStatus && (
                <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${statusPill[myStatus] || statusPill.pending}`}>{myStatus}{module.my_completion?.last_attempt_score != null ? ` · ${module.my_completion.last_attempt_score}%` : ''}</span>
              )}
            </div>
            <h1 className="font-heading text-3xl font-bold text-slate-900 mt-2">{module.title}</h1>
            {module.description && <p className="text-slate-500 mt-2">{module.description}</p>}
          </div>
        </div>

        {/* Materials */}
        {module.materials?.length > 0 && (
          <div className="mt-6">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Materials</div>
            <div className="space-y-2">
              {module.materials.map((m, i) => {
                const href = m.file_id ? fileUrl(m.file_id) : m.url;
                const isFile = m.type === 'file' || !!m.file_id;
                return (
                  <a key={i} href={href} target="_blank" rel="noreferrer" data-testid={`material-${i}`}
                     className="flex items-center justify-between gap-3 bg-slate-50 hover:bg-slate-100 rounded-xl p-3 border border-slate-200">
                    <div className="flex items-center gap-2 min-w-0">
                      {isFile ? <FileText className="w-5 h-5 text-[#3D4A8E]" /> : <PlayCircle className="w-5 h-5 text-[#3D4A8E]" />}
                      <div className="min-w-0">
                        <div className="font-medium text-sm truncate">{m.label || (m.file_id ? 'Document' : m.url)}</div>
                        {isFile && <div className="text-[11px] text-slate-500">Uploaded file · click to open in new tab</div>}
                      </div>
                    </div>
                    <ExternalLink className="w-4 h-4 text-slate-400" />
                  </a>
                );
              })}
            </div>
          </div>
        )}

        {/* Quiz or Mark done */}
        {!isAdmin && myStatus !== 'completed' && (
          module.quiz?.length > 0 ? (
            <div className="mt-6">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Quiz · 80% required to pass</div>
              {module.quiz.map((q, qi) => (
                <div key={qi} data-testid={`quiz-q-${qi}`} className="bg-slate-50 rounded-xl p-4 mb-3 border border-slate-200">
                  <div className="font-semibold text-sm mb-2">{qi + 1}. {q.q}</div>
                  {q.options.map((opt, oi) => (
                    <label key={oi} className="flex items-center gap-2 cursor-pointer text-sm py-1 hover:text-[#3D4A8E]">
                      <input type="radio" name={`q-${qi}`} value={oi} data-testid={`q-${qi}-opt-${oi}`}
                             onChange={() => setAnswers({ ...answers, [qi]: oi })}
                             checked={answers[qi] === oi} />
                      {opt}
                    </label>
                  ))}
                </div>
              ))}
              <Button onClick={submitQuiz} disabled={submitting} data-testid="submit-quiz"
                      className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]">
                <Award className="w-4 h-4 mr-1.5" /> {submitting ? 'Submitting…' : 'Submit quiz'}
              </Button>
              {result && (
                <div className={`mt-4 rounded-xl p-4 border ${result.passed ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
                  {result.passed ? <CheckCircle2 className="w-5 h-5 inline mr-2" /> : <XCircle className="w-5 h-5 inline mr-2" />}
                  Score: <b>{result.score}%</b> ({result.correct}/{result.total}). {result.passed ? 'Completed!' : `Need ${result.pass_score}%. You can retry above.`}
                </div>
              )}
            </div>
          ) : (
            <div className="mt-6">
              <Button onClick={markDone} disabled={submitting} data-testid="mark-done-btn"
                      className="rounded-full bg-emerald-600 hover:bg-emerald-700">
                <CheckCircle2 className="w-4 h-4 mr-1.5" /> {submitting ? 'Saving…' : 'I have completed this training'}
              </Button>
            </div>
          )
        )}
      </div>

      {/* Admin completion table */}
      {isAdmin && completions && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="completion-report">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Completion report</div>
              <h2 className="font-heading text-xl font-semibold text-slate-900 mt-1 inline-flex items-center gap-2"><Users className="w-5 h-5" /> Who&apos;s done &amp; who&apos;s pending</h2>
            </div>
            <div className="flex gap-4 text-sm">
              <span><b className="text-emerald-700">{completions.summary.completed}</b> completed</span>
              <span><b className="text-red-700">{completions.summary.failed}</b> failed</span>
              <span><b className="text-amber-700">{completions.summary.pending}</b> pending</span>
            </div>
          </div>
          <div className="divide-y divide-slate-100">
            {completions.rows.map(r => (
              <div key={r.user_id} className="py-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="font-semibold text-sm text-slate-900">{r.name}</div>
                  <div className="text-xs text-slate-500">{r.title || '—'}{r.department ? ` · ${r.department}` : ''}</div>
                </div>
                <div className="flex items-center gap-2">
                  {r.last_attempt_score != null && <span className="text-xs text-slate-500">{r.last_attempt_score}%</span>}
                  {r.attempts > 0 && <span className="text-xs text-slate-400">{r.attempts} attempt{r.attempts !== 1 ? 's' : ''}</span>}
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full border ${statusPill[r.status] || statusPill.pending}`}>{r.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
